import React, { useState } from 'react';
import SEO from '../../components/SEO';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FaTelegram, FaWhatsapp, FaDownload, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { TestResult } from '../../types/tests';
import { shareTelegram, shareWhatsApp, downloadResults } from '../../utils/shareResults';
import ResultScale from '../../components/ResultScale/ResultScale';
import './TestResults.css';

interface LocationState {
    result?: TestResult;
}

const TestResults: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { testId } = useParams<{ testId: string }>();
    const state = location.state as LocationState;
    const { result } = state; // Moved this line up to make 'result' available for the check
    const [showDetails, setShowDetails] = useState(false);

    if (!result) {
        return (
            <div className="main-content-container">
                <div className="page-header">Результаты не найдены</div>
                <div className="test-results-container">
                    <button className="result-button" onClick={() => navigate('/materials/tests')}>
                        Вернуться к списку тестов
                    </button>
                </div>
            </div>
        );
    }



    const handleRetakeTest = () => {
        navigate(`/materials/test/${testId}`);
    };

    const handleBrowseTests = () => {
        navigate('/materials/tests');
    };

    return (
        <div className="main-content-container">
            <SEO
                title={`Результаты теста: ${result.test?.title || 'Психологический тест'}`}
                description="Результаты психологического тестирования."
                noIndex={true} // Don't index personal results
            />
            <div className="test-results-container">
                <div className="result-header">
                    <h2 className="result-title">Ваш результат</h2>
                </div>

                {result.category && <div className="result-category">{result.category}</div>}
                {result.subScores && result.subScores.length > 0 ? (
                    <div className="subscales-results">
                        {result.subScores.map((subScore) => {
                            // Calculate max score for subscale to visualize scale correctly
                            const subscaleQuestions = result.test?.questions.filter(q => q.subscale === subScore.subscaleId) || [];
                            const maxPossible = subscaleQuestions.reduce((acc, q) => acc + Math.max(...q.answers.map(a => a.score)), 0);
                            const minPossible = subscaleQuestions.reduce((acc, q) => acc + Math.min(...q.answers.map(a => a.score)), 0);

                            // Find subscale config in test definition to pass interpretations to ResultScale
                            const subscaleConfig = result.test?.subscales?.find(s => s.id === subScore.subscaleId);

                            return (
                                <div key={subScore.subscaleId} className="subscale-result-item">
                                    <h3>{subScore.subscaleTitle}</h3>
                                    <div className="result-score">Баллы: {subScore.score}</div>
                                    <div className="result-category">{subScore.category}</div>

                                    <ResultScale
                                        score={subScore.score}
                                        minScore={minPossible}
                                        maxScore={maxPossible}
                                        interpretations={subscaleConfig?.interpretations || []}
                                        higherIsBetter={false} // HADS is standard: lower is better
                                    />

                                    <div className="result-interpretation">
                                        <p>{subScore.longDescription || subScore.interpretation}</p>
                                    </div>
                                    <div className="result-divider" />
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <>
                        <div className="result-score">Баллы: {result.totalScore}</div>

                        {result.test && result.test.questions && (
                            <ResultScale
                                score={result.totalScore}
                                minScore={result.test.questions.reduce((acc, q) => acc + Math.min(...q.answers.map(a => a.score)), 0)}
                                maxScore={result.test.questions.reduce((acc, q) => acc + Math.max(...q.answers.map(a => a.score)), 0)}
                                interpretations={result.test.interpretations || []}
                                higherIsBetter={result.test.higherIsBetter}
                            />
                        )}

                        <div className="result-divider" />

                        {result.interpretation && (
                            <div className="result-interpretation">
                                <h3>Интерпретация</h3>
                                <p>{result.interpretation}</p>
                            </div>
                        )}

                        {result.longDescription && (
                            <div className="result-description">
                                <p>{result.longDescription}</p>
                            </div>
                        )}
                    </>
                )}

                <div className="result-divider" />

                {result.test && result.userResponses && (
                    <>
                        <div className="detailed-results">
                            <div
                                className="detailed-results-header"
                                onClick={() => setShowDetails(!showDetails)}
                            >
                                <h3>Ваши ответы</h3>
                                {showDetails ? <FaChevronUp /> : <FaChevronDown />}
                            </div>

                            {showDetails && (
                                <div className="questions-list">
                                    {result.test.questions.map((question, index) => {
                                        const response = result.userResponses?.find(r => r.questionId === question.id);
                                        const selectedAnswer = question.answers.find(a => a.id === response?.answerId);

                                        return (
                                            <div key={question.id} className="result-question-item">
                                                <div className="question-number">Вопрос {index + 1}</div>
                                                <div className="question-text">{question.text}</div>
                                                <div className="selected-answer">
                                                    <span className="answer-label">Ваш ответ:</span>
                                                    <span className="answer-value">{selectedAnswer?.text || 'Не отвечено'}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        <div className="result-divider" />
                    </>
                )}

                <div className="share-section">
                    <h3>Поделиться результатами</h3>
                    <div className="share-buttons">
                        <button
                            className="share-button telegram"
                            onClick={() => shareTelegram(result)}
                        >
                            <FaTelegram size={20} />
                            <span>Telegram</span>
                        </button>
                        <button
                            className="share-button whatsapp"
                            onClick={() => shareWhatsApp(result)}
                        >
                            <FaWhatsapp size={20} />
                            <span>WhatsApp</span>
                        </button>
                        <button
                            className="share-button download"
                            onClick={() => downloadResults(result)}
                        >
                            <FaDownload size={18} />
                            <span>Скачать</span>
                        </button>
                    </div>
                </div>

                <div className="result-divider" />

                <div className="result-actions">
                    <button className="result-button" onClick={handleRetakeTest}>
                        Пройти тест заново
                    </button>
                    <button className="result-button result-button-primary" onClick={handleBrowseTests}>
                        Смотреть другие тесты
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TestResults;
